<script lang="ts" setup>
import { set, get } from '@vueuse/core'
import { DateTime, Option, Record, Effect, pipe } from 'effect'
import { advent24RoundUsers, cutoffs } from '~/lib/dates'
import type { advent24Rounds } from '~/models/submission'

/*
 * TODO: Add loading indicator
 */

type CutoffMatchOption = Option.Option<readonly [round: typeof advent24Rounds[number], date: DateTime.DateTime]>
const emptyTuple: CutoffMatchOption = Option.none() as CutoffMatchOption

Effect.succeed(Option.none<number>()).pipe(
  Effect.flatMap(a => Math.random() > 1 ? Effect.succeed(a) : Effect.succeed(a)),
)

const matchRound = () => Effect.runPromise(Record.reduce(
  cutoffs,
  Effect.succeed<CutoffMatchOption>(emptyTuple),
  (acc, b, c) => Effect.flatMap(acc, o => pipe(
    o,
    Option.match({
      onSome: () => Effect.succeed(o),
      onNone: () => Effect.if(DateTime.isFuture(b), {
        onTrue: () => Effect.succeed(Option.some([c, b] as const)),
        onFalse: () => Effect.succeed(o),
      }),
    }),
  )),
))

const currentMatch = ref<CutoffMatchOption>(emptyTuple)

const round = ref<string>()
const user = ref<string>()
const date = ref<Date>()
const updateCurrentMatch = async () => set(currentMatch, await matchRound())

updateCurrentMatch()

useIntervalFn(updateCurrentMatch, 1000)

watchEffect(() => {
  const match = Option.getOrNull(get(currentMatch))
  if (match) {
    set(round, match[0])
    set(date, DateTime.toDate(match[1]))
    set(user, advent24RoundUsers[match[0]])
  }
})
</script>

<template>
  <div>
    <h3 class="font-bold">
      Igangværende runde:
    </h3>
    <template v-if="date">
      <div class="py-4">
        <span class="text-zinc-400">{{ round }} - </span>
        <span class="capitalize">{{ user }}</span>
      </div>
      <CurrentRoundRevealCounter
        v-model="date"
        class="w-full"
      />
    </template>
    <template v-else>
      <USkeleton class="h-5 w-[150px] my-5" />
      <USkeleton class="h-5 w-[250px]" />
    </template>
    <div class="pt-4 text-sm">
      <h3 class="font-bold pb-2">
        Oversigt
      </h3>
      <div
        v-for="[o_rnd, o_date], index in Object.entries(cutoffs).map(([k, d]): [string, Date] => [k, DateTime.toDate(d)])"
        :key="index"
        class="w-full pb-1"
      >
        <span class="text-zinc-500">{{ o_rnd }} - </span>
        <span class="capitalize">{{ advent24RoundUsers[o_rnd as keyof typeof cutoffs] }}</span>
        <span class="pl-2 text-zinc-500">(afsløres d. <CurrentRoundDate :datetime="o_date" />)</span>
      </div>
    </div>
  </div>
</template>
